if (!customElements.get('best-sellers-carousel')) {
  class BestSellersCarousel extends HTMLElement {
    constructor() {
      super();
      this._layoutRefreshRaf = null;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-best-sellers-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-best-sellers-panel]'));
      this.viewAllLink = this.querySelector('[data-best-sellers-view-all]');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.activateTab(tab.dataset.bestSellersTab);
        });
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', (event) => this.onButtonClick(event));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', (event) => this.onButtonClick(event));
      }

      this._onScroll = () => this.updateArrowState();
      this._onResize = () => this.refreshLayout();
      this._onSectionLoad = (event) => {
        if (event.target.contains(this)) this.refreshLayout();
      };

      window.addEventListener('resize', this._onResize);
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      this.bindActiveSlider();
      this.updateViewAllLink();
      this.refreshLayout();
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onSectionLoad) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (this.activeSlider) {
        this.activeSlider.removeEventListener('scroll', this._onScroll);
      }
      if (this._layoutRefreshRaf) cancelAnimationFrame(this._layoutRefreshRaf);
    }

    getActivePanel() {
      return this.panels.find((panel) => panel.classList.contains('is-active')) || this.panels[0];
    }

    bindActiveSlider() {
      if (this.activeSlider) {
        this.activeSlider.removeEventListener('scroll', this._onScroll);
      }

      const panel = this.getActivePanel();
      this.activeSlider = panel ? panel.querySelector('[data-best-sellers-track]') : null;
      this.sliderItems = this.activeSlider
        ? Array.from(this.activeSlider.querySelectorAll('[data-best-sellers-slide]'))
        : [];

      if (this.activeSlider) {
        this.activeSlider.addEventListener('scroll', this._onScroll, { passive: true });
      }
    }

    activateTab(tabId) {
      if (!tabId) return;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.bestSellersTab === tabId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.bestSellersPanel === tabId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.bindActiveSlider();
      this.updateViewAllLink();

      if (this.activeSlider) {
        this.activeSlider.scrollTo({ left: 0 });
      }

      this.refreshLayout();
    }

    updateViewAllLink() {
      if (!this.viewAllLink) return;

      const panel = this.getActivePanel();
      const collectionUrl = panel ? panel.dataset.collectionUrl : '';

      if (collectionUrl) {
        this.viewAllLink.setAttribute('href', collectionUrl);
        this.viewAllLink.removeAttribute('role');
        this.viewAllLink.removeAttribute('aria-disabled');
      } else {
        this.viewAllLink.removeAttribute('href');
        this.viewAllLink.setAttribute('role', 'link');
        this.viewAllLink.setAttribute('aria-disabled', 'true');
      }
    }

    getSliderItemOffset() {
      if (!this.sliderItems || this.sliderItems.length < 2) {
        return this.sliderItems[0] ? this.sliderItems[0].getBoundingClientRect().width : 0;
      }

      return this.sliderItems[1].offsetLeft - this.sliderItems[0].offsetLeft;
    }

    onButtonClick(event) {
      event.preventDefault();
      if (!this.activeSlider) return;

      const offset = this.getSliderItemOffset();
      if (!offset) return;

      const maxScroll = this.activeSlider.scrollWidth - this.activeSlider.clientWidth;
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      const nextPosition = this.activeSlider.scrollLeft + direction * offset;

      this.activeSlider.scrollTo({
        left: Math.max(0, Math.min(nextPosition, maxScroll)),
        behavior: 'smooth',
      });
    }

    updateArrowState() {
      if (!this.prevButton || !this.nextButton) return;

      if (!this.activeSlider) {
        this.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
        this.prevButton.setAttribute('disabled', 'disabled');
        this.nextButton.setAttribute('disabled', 'disabled');
        return;
      }

      const maxScroll = this.activeSlider.scrollWidth - this.activeSlider.clientWidth;
      const hasScroll = maxScroll > 1;
      const atStart = this.activeSlider.scrollLeft <= 1;
      const atEnd = this.activeSlider.scrollLeft >= maxScroll - 1;

      this.classList.toggle('is-scrollable', hasScroll);
      this.classList.toggle('is-at-start', atStart);
      this.classList.toggle('is-at-end', atEnd);

      if (!hasScroll || atStart) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (!hasScroll || atEnd) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }

    refreshLayout() {
      if (this._layoutRefreshRaf) cancelAnimationFrame(this._layoutRefreshRaf);

      this._layoutRefreshRaf = requestAnimationFrame(() => {
        this._layoutRefreshRaf = requestAnimationFrame(() => {
          this._layoutRefreshRaf = null;
          this.bindActiveSlider();
          this.updateArrowState();
        });
      });
    }
  }

  customElements.define('best-sellers-carousel', BestSellersCarousel);
}
